import { NamedView, View } from "@common/types/views/views";
import { isEqual } from "@common/helpers/comparison";
import { useMemo } from "react";

interface UseViewComparisonOptions<T extends View> {
    view: T;
    baseView: NamedView<T> | null;
    defaultFilters: T["filters"];
}

interface UseViewComparisonReturn {
    isDirty: boolean;
}

export function useViewComparison<T extends View>(
    options: UseViewComparisonOptions<T>,
): UseViewComparisonReturn {
    const { view, baseView, defaultFilters } = options;

    const isDirty = useMemo(() => {
        if (!baseView) {
            return false;
        }

        const currentViewComparable = {
            type: view.type,
            filters: {
                ...defaultFilters,
                ...view.filters,
            },
            columns: view.columns,
            options: view.options,
        };

        const baseViewComparable = {
            type: baseView.type,
            filters: {
                ...defaultFilters,
                ...baseView.filters,
            },
            columns: baseView.columns,
            options: baseView.options,
        };

        return !isEqual(currentViewComparable, baseViewComparable);
    }, [view, baseView, defaultFilters]);

    return {
        isDirty,
    };
}
